import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, ComponentLoader, Heading, InPageAlert, SectionPage } from '@snsw-gel/react';
import { AuthContext } from 'app/sharedServices/authentication/AuthContext';
import { getEventDetails } from 'app/services/api/internal/API';
import { getLoginUrl } from 'app/sharedServices/authentication/authentication';
import { setStorageItem } from 'app/sharedServices/storage/storage';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import EventLayout from './EventLayout';

interface EventLandingProps {
  eventId: string | null;
}

const isValidUUID = (id: string | null): boolean =>
  id !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

const EventLanding: React.FC<EventLandingProps> = ({ eventId }) => {
  // Read the agency name from the URL path (/events/:agencyName/landing).
  const { agencyName } = useParams<{ agencyName: string }>();
  const { search } = useLocation();
  // The review step redirects here with isFullyBooked=true when a booking attempt
  // returns a 409 "fully booked" from the backend. Seed the fully-booked state from
  // that flag so the friendly message is shown immediately, without re-fetching.
  const isFullyBookedParam = new URLSearchParams(search).get('isFullyBooked') === 'true';
  const [validating, setValidating] = useState(false);
  const [isFullyBooked, setIsFullyBooked] = useState(isFullyBookedParam);
  const [isAlreadyBooked, setIsAlreadyBooked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const { checkIsAuthenticated } = useContext(AuthContext);
  const { setEventDetails, setConsent } = useEventFlow();
  const isAuthenticated = checkIsAuthenticated();

  const isInvalidEventId = !isValidUUID(eventId);

  const useOfflineMyAccount = process.env.REACT_APP_OFFLINE_MY_ACCOUNT === 'true';

  const validateAndProceed = useCallback(async () => {
    setValidating(true);

    // In offline mode, ensure a valid fake JWT exists so the API receives a customer ID
    if (useOfflineMyAccount) {
      const exp = Math.floor(Date.now() / 1000) + 3600; // 1 hour from now
      const fakePayload = btoa(
        JSON.stringify({ sub: 'f6b201fe-b762-4138-b4f9-adfc4c41bde9', email: 'joe.bloggs@service.nsw.gov.au', exp }),
      )
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      setStorageItem('idToken', `eyJ0eXAiOiJKV1QiLCJhbGciOiJub25lIn0.${fakePayload}.`, 'local');
    }

    try {
      const response = await getEventDetails(eventId);
      const data = (response as any)?.data || response;

      if (!data) {
        throw new Error('Event detail not returned');
      }

      setEventDetails(data);

      if (agencyName) {
        sessionStorage.setItem('currentAgencyName', agencyName);
      }

      if (data.alreadyBooked) {
        setIsAlreadyBooked(true);
      } else if (data.isFullyBooked) {
        setIsFullyBooked(true);
      } else {
        // Record the Profile Connect transaction type for this event so the
        // personal details page can decide whether and how to hand off to
        // Profile Connect. The hand-off itself happens on the personal details
        // page (mirroring the service flow's UserDetails), not here.
        const transactionType = data.profileConnectTransactionType || 'NAME_CONTACT';
        setStorageItem('profileConnectTransactionType', transactionType, 'session');

        history.push(`/events/${agencyName}/details`);
        return;
      }
    } catch {
      setError('Unable to load event details. Please try again later.');
      return;
    } finally {
      setValidating(false);
    }
  }, [useOfflineMyAccount, eventId, agencyName, history, setEventDetails]);

  // After login redirect back, validate event eligibility
  useEffect(() => {
    const shouldResume = sessionStorage.getItem('resumeEventJourneyAfterLogin') === 'true';

    if (isAuthenticated && shouldResume) {
      sessionStorage.removeItem('resumeEventJourneyAfterLogin');
      validateAndProceed();
    }
  }, [isAuthenticated, validateAndProceed]);

  const onNext = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Event-flow equivalent of the SBA landing page's `hasGivenConsent` gate, which the
    // EventRouter redirect guard and the post-login resume effect rely on to confirm the
    // user passed through this landing step.
    //
    // NOTE: this is hardcoded to true, not config-driven. The SBA landing derives consent
    // from its page config's `declarations` array; that array is currently empty, so SBA
    // resolves to true on Continue and consent is implied by the displayed privacy notice.
    // We hardcode the same outcome here because the event flow has no declarations config.
    // This is fine only as long as event landings never need consent checkboxes — if that
    // changes, this must become config-driven like SBA rather than an unconditional true.
    setConsent(true);
    sessionStorage.setItem('homePath', window.location.pathname);

    if (isAuthenticated) {
      // Already logged in — validate directly
      await validateAndProceed();
    } else if (useOfflineMyAccount) {
      // Fake auth mode — validate directly (token is set in validateAndProceed)
      setStorageItem('isAuthenticated', true, 'session');
      await validateAndProceed();
    } else {
      // Not logged in — redirect to login, will come back to this page
      sessionStorage.setItem('resumeEventJourneyAfterLogin', 'true');
      setStorageItem('nextPath', `/events/${agencyName}/landing?eventCode=${eventId}`, 'session');
      try {
        const loginUrl = await getLoginUrl();
        if (loginUrl) {
          window.location.assign(loginUrl);
        }
      } catch {
        console.error('Could not get login url');
      }
    }
  };

  if (isInvalidEventId) {
    return (
      <EventLayout showStepper={false}>
        <InPageAlert variant="error" title="Invalid event link">
          <p>The event link you followed is not valid. Please check the URL and try again.</p>
        </InPageAlert>
      </EventLayout>
    );
  }

  if (validating) {
    return <ComponentLoader label="Loading" fullPage />;
  }

  // Show "already booked" state
  if (isAlreadyBooked) {
    return (
      <EventLayout showStepper={false}>
        <InPageAlert variant="info" title="You're already registered for this event">
          <p>
            You do not need to register again. You can manage or cancel your registration using the link in your
            confirmation email.
          </p>
        </InPageAlert>
      </EventLayout>
    );
  }

  // Show "fully booked" state
  if (isFullyBooked) {
    return (
      <EventLayout showStepper={false}>
        <InPageAlert variant="warning" title="This event is fully booked">
          {/*<p>*/}
          {/*  You can browse other <a href="/events">upcoming events</a>.*/}
          {/*</p>*/}
        </InPageAlert>
      </EventLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <EventLayout showStepper={false}>
        <InPageAlert variant="error" title="Something went wrong">
          <p>{error}</p>
        </InPageAlert>
      </EventLayout>
    );
  }

  // Default: consent form
  return (
    <EventLayout showStepper={false}>
      <Heading level={3}>Privacy</Heading>
      <p>
        The{' '}
        <a
          href="https://www.service.nsw.gov.au/privacy-collection-notices/small-business-advisory-program"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Collection Notice
        </a>{' '}
        explains how Service NSW will collect and use your personal information.
      </p>

      <SectionPage>
        <p>Log in to your MyServiceNSW Account to continue.</p>
        <Button id="eventLandingNext" onClick={onNext}>
          Continue
        </Button>
      </SectionPage>
    </EventLayout>
  );
};

export default EventLanding;
